import { Manager } from '../../domain/Manager';
import { ManagerProfile } from '../../domain/ManagerProfile';
import { ManagerRole } from '../../domain/ManagerRole';

type ManagerProfilePayload = Pick<ManagerProfile, 'uuid' | 'email' | 'firstName' | 'lastName'>;

interface MentorPayload {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  is_admin?: boolean;
  admin_type: string | null;
  picture?: string;
  pending_password_reset?: boolean;
  password_expiration_date?: string;
}

const mapMentorPayloadToManager = (mentor: MentorPayload | null | undefined): Manager | null => {
  // TODO: gate on is_admin alone since admin_type is nullable on legacy Mentor records
  // (no backend code path sets it, only manual Django admin edits) and external IMs were
  // being misclassified as mentors. Revert to requiring admin_type once the backend backfills
  // existing null values and sets it on creation for every admin Mentor record.
  if (!mentor?.is_admin) return null;

  return Manager.create({
    id: mentor.id,
    firstName: mentor.first_name,
    lastName: mentor.last_name,
    email: mentor.email,
    role:
      mentor.admin_type === ManagerRole.BRIDGE_STAFF
        ? ManagerRole.BRIDGE_STAFF
        : ManagerRole.EXTERNAL,
    picture: mentor.picture,
    pendingPasswordReset: mentor.pending_password_reset,
    passwordExpirationDate: mentor.password_expiration_date,
  });
};

// The backend keeps Bridge staff on the legacy admin Mentor record, so an account with only a v2
// manager profile is always EXTERNAL.
const mapManagerProfileToManager = (
  profile: ManagerProfilePayload | null | undefined
): Manager | null => {
  if (!profile) return null;

  return Manager.create({
    id: null,
    uuid: profile.uuid,
    firstName: profile.firstName,
    lastName: profile.lastName,
    email: profile.email,
    role: ManagerRole.EXTERNAL,
  });
};

const resolveCurrentManager = (
  mentor: MentorPayload | null | undefined,
  managerProfile: ManagerProfilePayload | null | undefined
): Manager | null => mapMentorPayloadToManager(mentor) ?? mapManagerProfileToManager(managerProfile);

export { mapMentorPayloadToManager, mapManagerProfileToManager, resolveCurrentManager };
export type { MentorPayload, ManagerProfilePayload };
